import React, { useState } from "react";

/**
 * TextInput component that provides a single-line text input with debounced updates
 * Features character limit control, error state display, and optional required validation
 *
 * @param {Object} props - Component props
 * @param {Object} props.field - Field configuration object (currently unused but available for future extensions)
 * @param {Object} props.properties - Current form properties/values
 * @param {string} props.property - The property key this input manages
 * @param {Function} props.updateProperties - Callback to update parent component properties
 * @param {number} props.maxLength - Maximum character limit (default: 1000)
 * @param {boolean} props.required - Whether the field is required (default: false)
 * @param {boolean} props.error - Whether to display error state styling (default: false)
 */
const TextInput = ({
    field,
    properties,
    property,
    updateProperties,
    maxLength = 1000,
    required = false,
    error = false,
}) => {
    // State to store the typing timeout ID for debouncing
    const [typingTimeout, setTimingTimeout] = useState(0);

    /**
     * Handle input value changes with debouncing
     * Updates immediately for UI responsiveness, then waits 300ms before triggering parent update
     * Uses same short delay as TextArea for consistent text input behavior
     *
     * @param {Event} e - The input change event
     */
    function updateValue(e) {
        // Clone properties and update the specific property
        let props = { ...properties };
        props[property] = e.target.value;

        // Update immediately without triggering side effects (false parameter)
        updateProperties(props, false);

        // Clear existing timeout to restart debounce timer
        if (typingTimeout) {
            clearTimeout(typingTimeout);
        }

        // Set new timeout for delayed update with side effects
        setTimingTimeout(
            setTimeout(() => {
                updateProperties(props); // Trigger full update after delay
            }, 300),
        );
    }

    return (
        <div className={"row forminput "}>
            <div className="col-sm-12 input-group">
                <input
                    type="text"
                    className={`form-control ${error ? "is-invalid" : ""}`}
                    name={property}
                    onChange={updateValue}
                    maxLength={maxLength}
                    value={properties[property] || ""}
                />
            </div>
        </div>
    );
};

export default TextInput;
